import { useMapContext } from "@components/sections/mapSection/MapProvider";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/ui/accordion";
import { assertUnreachable } from "@lib/utils/assertUnreachable";
import {
  isFlatMultipleOptions,
  isNestedMultipleOptions,
  isSingleOption,
} from "@lib/utils/types/filters";
import { OnSelect } from "@lib/utils/types/filters/OnSelect";
import { ToggleAll } from "@lib/utils/types/filters/ToggleAll";
import { MapFilter } from "@repo/shared-types";

import { MultiSelect } from "../../ui/form/MultiSelect";
import { NestedMultiSelect } from "../../ui/form/NestedMultiSelect";
import { RadioGroup } from "../../ui/form/RadioGroup";
import { Query } from "./mapForm.hooks";

interface IMapFormFieldMapper {
  filter: MapFilter;
  onSelect: (v: OnSelect) => void;
  toggleAll: (v: ToggleAll) => void;
  query: Query<string[]>;
}

export function MapFormFieldMapper({
  filter,
  onSelect,
  toggleAll,
  query,
}: IMapFormFieldMapper) {
  const { setFilter } = useMapContext();

  const onFilterSelection = (filter: OnSelect) => {
    onSelect(filter);
    setFilter(filter);
  };

  function mapPropsToFilterElement() {
    if (isSingleOption(filter))
      return (
        <RadioGroup
          selectedValue={query[filter.values[0].name][0]}
          clearLabel="Alle"
          onSelect={onFilterSelection}
          values={filter.values}
        />
      );
    if (isFlatMultipleOptions(filter))
      return (
        <MultiSelect
          values={filter.values}
          selectedValues={query[filter.values[0].name]}
          onSelect={onFilterSelection}
        />
      );
    if (isNestedMultipleOptions(filter))
      return (
        <>
          {filter.values.map(value => (
            <NestedMultiSelect
              indicator="etaper"
              key={value.id}
              {...value}
              selectedValues={query[filter.values[0].value[0].name]}
              onSelect={onFilterSelection}
              toggleAll={all => {
                toggleAll(all);
                setFilter(null);
              }}
            />
          ))}
        </>
      );
    return assertUnreachable(filter);
  }

  return (
    <AccordionItem
      value={filter.id}
      className="w-full border-t border-t-[#AAAAAA]"
    >
      <AccordionTrigger className="m-w-full w-full font-bold data-[state=open]:border-b-[#AAAAAA]">
        {filter.label}
      </AccordionTrigger>
      <AccordionContent>{mapPropsToFilterElement()}</AccordionContent>
    </AccordionItem>
  );
}
